/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-unused-vars */
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material'
import { VATTotalPrice } from 'api/common-types'
import { JourneyType } from 'app/constants/Booking/BookingConstants'
import { BookingSummary } from 'components/Bookings/BookingSummary/BookingSummary'
import {
  mergeObjects,
  multiplyValues,
} from 'components/Common/ObjectManagement/ObjectManagement'
import { StepComponentProps } from 'components/RouteWizard/RouteWizard'
import { useWizard } from 'components/RouteWizard/Wizard'
import useBooking from 'hooks/useBooking'
import useClients from 'hooks/useClients'
import useQuote from 'hooks/useQuote'
import { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import {
  fireBookingCreated,
  fireQuoteSelected,
  fireQuotesShown,
  flattenGroupedPrices,
} from 'analytics/bookingEvents'
import VehicleCard from './VehicleCard'

const SURFACE = 'quote_old' as const

const TransportOptions = ({ onSuccess }: StepComponentProps) => {
  const { data } = useWizard()
  const history = useHistory()
  const [selectMultiple, setSelectMultiple] = useState(false)
  const [selectedVehicles, setSelectedVehicles] = useState({})
  const [canSelectMoreCars, setCanSelectMoreCars] = useState(true)
  const [eta, setEta] = useState<number>(0)
  const [showSeatCountWarning, setShowSeatCountWarning] = useState(false)
  const [preview, setPreview] = useState({
    ...data.preview,
    estimatedPrice: 0,
    estimatedPriceCurrency: '',
    selectedVehicle: null,
  })
  const { clients } = useClients()

  const {
    prices,
    reference,
    inboundReference,
    createQuote,
    isLoading,
    error,
    errorDetails,
  } = useQuote()
  console.log('quoteData: Transport', prices)
  const {
    bookingData,
    createBooking,
    isLoading: isLoadingBooking,
  } = useBooking()
  console.log(isLoadingBooking, isLoading)
  const accountNumber = clients?.find(
    (c: any) => c.id === data.account?.clientId,
  )?.accountNumber
  const firstValidationError = errorDetails?.validationErrors?.[0]
  const quoteErrorStatus = errorDetails?.status
  const quoteErrorCode = errorDetails?.code
  const isServerQuoteError =
    (typeof quoteErrorStatus === 'number' &&
      quoteErrorStatus >= 500 &&
      quoteErrorStatus < 600) ||
    (typeof quoteErrorStatus !== 'number' &&
      typeof quoteErrorCode === 'string' &&
      quoteErrorCode.startsWith('SYS'))

  const handleCreateQuote = () => {
    if (!accountNumber) {
      return
    }

    const isImmediateJourney =
      data.plan?.journeyTypeName === JourneyType.ONE_WAY &&
      data.plan?.isImmediateJourney

    const getPassengers = (passengers: any) => {
      if (
        passengers === undefined ||
        passengers === null ||
        passengers.length === 0
      ) {
        return []
      }
      return passengers
        .filter((p: any) => !(p && Object.keys(p).length === 0))
        .map((p: any) => ({
          contactNumber: p.contactNumber,
          email: p.email,
          isLeadPassenger: p.isLeadPassenger,
          name: p.name,
          userId: p.userId,
        }))
    }
    console.log('quoteData: Transport', data)
    const quoteData = {
      clientAccountNumber: accountNumber,
      onBehalfOf: data.account?.onBehalfOf?.id
        ? data.account?.onBehalfOf.id
        : '',
      numberOfPassengers: data.account?.numberOfPassengers,
      passengers: [
        ...getPassengers(data.account?.passengers),
        data.account?.mainPassenger,
      ],
      bookingReferences: data.account?.bookingReferenceValues
        ? data.account?.bookingReferenceValues.filter((ref: any) => ref?.value)
        : [],
      journeyTypeName: data.plan?.journeyTypeName,
      isImmediateJourney,
      waitBeforeReturnTime:
        data.plan?.journeyTypeName === JourneyType.WAIT_AND_RETURN
          ? (parseInt(data.plan?.waitHours, 10) || 0) * 60 +
            (parseInt(data.plan?.waitMinutes, 10) || 0)
          : null,
      directedJourneyTime:
        data.plan?.journeyTypeName === JourneyType.AS_DIRECTED
          ? parseInt(data.plan?.directedJourneyTime, 10) || 0
          : null,
      outbound: {
        ...data.plan?.outbound,
        additionalStops: data.plan?.additionalStops,
        when: DateTime.fromISO(data.plan?.outbound?.when).isValid
          ? DateTime.fromISO(data.plan?.outbound?.when)
              .setZone(data.plan?.outbound.timeZone, {
                keepLocalTime: true,
              })
              .toString()
          : DateTime.fromJSDate(data.plan?.outbound?.when)
              .setZone(data.plan?.outbound.timeZone, {
                keepLocalTime: true,
              })
              .toString(),
      },
      inbound: {
        ...data.plan?.inbound,
        when: DateTime.fromISO(data.plan?.inbound?.when).isValid
          ? DateTime.fromISO(data.plan?.inbound?.when)
              .setZone(data.plan?.outbound.timeZone, {
                keepLocalTime: true,
              })
              .toString()
          : DateTime.fromJSDate(data.plan?.inbound?.when)
              .setZone(data.plan?.outbound.timeZone, {
                keepLocalTime: true,
              })
              .toString(),
        timeZone: data.plan?.inbound ? data.plan?.outbound.timeZone : null,
      },
    }

    if (quoteData.bookingReferences?.length <= 0) {
      delete quoteData.bookingReferences
    }

    if (isImmediateJourney) {
      delete quoteData.outbound.when
    }

    if (quoteData.outbound?.additionalStops?.length <= 0) {
      delete quoteData.outbound?.additionalStops
    }

    if (data.plan?.outbound?.services == 'MeetAndGreet') {
      quoteData.outbound.services = [
        { serviceTypeName: 'MeetAndGreet', quantity: 1 },
      ]
    } else {
      delete quoteData.outbound.services
    }

    if (data.plan?.inbound?.services == 'MeetAndGreet') {
      quoteData.inbound.services = [
        { serviceTypeName: 'MeetAndGreet', quantity: 1 },
      ]
    } else {
      delete quoteData.inbound.services
    }

    if (quoteData.journeyTypeName !== JourneyType.RETURN) {
      delete quoteData.inbound
    }

    if (quoteData.outbound.pickup.country === 'United Kingdom') {
      // quoteData.outbound.timeZone = 'Europe/Madrid'
    }

    createQuote(quoteData)
  }

  const quotesShownFiredRef = useRef<string | null>(null)
  useEffect(() => {
    if (!prices || !Array.isArray(prices) || prices.length === 0) return
    const key = `${reference ?? ''}|${inboundReference ?? ''}`
    if (quotesShownFiredRef.current === key) return
    quotesShownFiredRef.current = key
    const flatQuotes = Array.isArray(prices)
      ? prices
      : flattenGroupedPrices(prices as any)
    fireQuotesShown({
      surface: SURFACE,
      quoteGroupId: reference,
      quoteGroupIdInbound: inboundReference,
      journeyType: data?.plan?.journeyTypeName,
      bookingType: data?.type,
      passengers: data?.account?.numberOfPassengers,
      quotes: flatQuotes as any,
    })
  }, [prices, reference, inboundReference])

  const pendingBookingRef = useRef<{
    supplierQuoteReference?: string
    vehicleType?: string
    selectedQuote?: any
  }>({})
  const bookingCreatedFiredRef = useRef<string | null>(null)
  useEffect(() => {
    if (bookingData) {
      const bookingId = (bookingData as any)?.id
      const bookingRef = (bookingData as any)?.bookingReference
      if (bookingRef && bookingCreatedFiredRef.current !== bookingRef) {
        bookingCreatedFiredRef.current = bookingRef
        const toNum = (v: any) =>
          typeof v === 'number'
            ? Number(v.toFixed(2))
            : typeof v === 'string' && v.length
            ? Number(parseFloat(v).toFixed(2))
            : undefined
        const full = (preview as any)?.estimatedPriceFull
        fireBookingCreated({
          surface: SURFACE,
          bookingId,
          bookingReference: bookingRef,
          quoteGroupId: reference,
          quoteGroupIdInbound: inboundReference,
          supplierQuoteReference: pendingBookingRef.current.supplierQuoteReference,
          vehicleType:
            pendingBookingRef.current.vehicleType ??
            (preview as any)?.selectedVehicle,
          winningPrice: toNum(full?.total ?? full),
          winningPriceExVat: toNum(full?.price),
          currency: full?.currency ?? (preview as any)?.estimatedPriceCurrency,
          journeyType: data?.plan?.journeyTypeName,
          bookingType: data?.type,
        })
      }
      onSuccess({
        transport: bookingData,
        prices: prices,
        preview,
      })
    }
  }, [bookingData])

  const handleBookNow = (
    supplierQuoteReference: string,
    numberOfSeats: number,
    estimatedPriceCurrency: string,
    price: number,
    title: string,
    inboundSupplierQuoteReference?: string,
  ) => {
    setShowSeatCountWarning(false)

    setPreview((prevValue: any) => ({
      ...prevValue,
      estimatedPriceCurrency: estimatedPriceCurrency,
      estimatedPriceFull: price,
      selectedVehicle: title,
    }))

    if (!supplierQuoteReference) {
      return
    }

    if (numberOfSeats < data?.account?.numberOfPassengers) {
      return setShowSeatCountWarning(true)
    }

    const bookingData: any = {
      outbound: {
        quoteReference: reference,
        vehicles: [
          {
            supplierQuoteReference,
            quantity: 1,
          },
        ],
      },
    }

    if (
      inboundSupplierQuoteReference &&
      data.plan?.journeyTypeName === JourneyType.RETURN
    ) {
      bookingData.inbound = {
        quoteReference: inboundReference,
        vehicles: [
          {
            supplierQuoteReference: inboundSupplierQuoteReference,
            quantity: 1,
          },
        ],
      }
    }

    const selectedQuote = (prices || []).find(
      (q: any) => q?.supplierQuoteReference === supplierQuoteReference,
    )
    pendingBookingRef.current = {
      supplierQuoteReference,
      vehicleType: title,
      selectedQuote,
    }
    fireQuoteSelected({
      surface: SURFACE,
      quoteGroupId: reference,
      selectedQuote: selectedQuote ?? { supplierQuoteReference, vehicleTypeName: title, price: { total: price, currency: estimatedPriceCurrency } },
      groupQuotes: prices as any,
      journeyType: data?.plan?.journeyTypeName,
    })

    createBooking(bookingData)
  }

  const handleMultiBooking = () => {
    let totalSeatCount = 0

    for (const [key, value] of Object.entries(selectedVehicles)) {
      const seatCount = value?.seats * value?.quantity
      totalSeatCount += seatCount
    }

    if (data.account?.numberOfPassengers > totalSeatCount) {
      setShowSeatCountWarning(true)
      return null
    }
    const vehicles = []
    for (const [key, value] of Object.entries(selectedVehicles)) {
      vehicles.push(value)
    }

    const bookingData: any = {
      outbound: {
        quoteReference: reference,
        vehicles,
      },
    }

    if (data.plan?.journeyTypeName === JourneyType.RETURN) {
      bookingData.inbound = {
        quoteReference: inboundReference,
        vehicles: vehicles.map((v: any) => ({
          ...v,
          supplierQuoteReference: v.inboundSupplierQuoteReference,
        })),
      }
    }

    const firstVehicle: any = vehicles[0]
    pendingBookingRef.current = {
      supplierQuoteReference: firstVehicle?.supplierQuoteReference,
      vehicleType: firstVehicle?.vehicleTypeName ?? firstVehicle?.title,
    }

    createBooking(bookingData)
  }

  useEffect(() => {
    handleCreateQuote()
  }, [clients, data?.account?.clientId])

  useEffect(() => {
    let estimatedPriceFull: VATTotalPrice = {
      price: 0,
      currency: '',
      vAT: 0,
      total: 0,
    }
    let totalVehicleCount = 0
    let selectedVehicle: any = Object.values(selectedVehicles)[0]

    for (const [key, value] of Object.entries(selectedVehicles)) {
      console.log('value', value)
      const vehicleCost = multiplyValues(value?.price, value?.quantity)
      // estimatedPrice += parseFloat(vehicleCost.toFixed(2))
      estimatedPriceFull = mergeObjects(estimatedPriceFull, vehicleCost, {
        exception: 'currency',
      })
      totalVehicleCount += value?.quantity
      console.log('estimatedPriceFull', estimatedPriceFull)
    }

    setCanSelectMoreCars(data.account?.numberOfPassengers > totalVehicleCount)
    setPreview((prevValue: any) => ({
      ...prevValue,
      estimatedPriceFull,
      selectedVehicle: selectedVehicle
        ? `${selectedVehicle.title}${totalVehicleCount > 1 ? ' +' : ''}`
        : null,
    }))
  }, [selectedVehicles])

  if (isLoading) {
    return (
      <Box
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <Typography align="center" paragraph>
          Getting your quotes, please wait.
        </Typography>
        <CircularProgress size={12} />
      </Box>
    )
  }

  if (!isLoading && error) {
    return (
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={7}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography align="center" paragraph>
              Sorry there was an error getting your quote.
            </Typography>
            <Alert severity="error">
              {!!errorDetails?.message && (
                <Typography
                  align="center"
                  paragraph
                  sx={{
                    maxWidth: '520px',
                    textWrap: 'pretty',
                    padding: '0.25rem',
                  }}
                >
                  {errorDetails.message}
                </Typography>
              )}
            </Alert>
            <Box
              component="div"
              sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}
            >
              {!isServerQuoteError && (
                <Button onClick={() => history.goBack()}>Go Back</Button>
              )}
              {isServerQuoteError && (
                <Button onClick={handleCreateQuote}>Try Again</Button>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={5}>
          <BookingSummary
            bookingPreview={preview}
            isLoadingBooking={isLoadingBooking}
            onSubmit={handleMultiBooking}
          />
        </Grid>
      </Grid>
    )
  }

  if (!isLoading && !error && !prices) {
    return (
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={7}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
            }}
          >
            <Typography align="center" paragraph>
              Sorry there are no quotes available for your journey.
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={5}>
          <BookingSummary
            bookingPreview={preview}
            isLoadingBooking={isLoadingBooking}
            onSubmit={handleMultiBooking}
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <>
      <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
        <Grid item xs={7}>
          <Box component="div" sx={{ float: 'right', marginRight: '40px' }}>
            <Box
              component="div"
              sx={{
                display: 'grid',
                gridTemplateColumns: '350px',
                gap: '30px',
                '@media (min-width: 1550px)': {
                  gridTemplateColumns: '350px 350px',
                },
              }}
            >
              {prices?.map((vehicle: any) => (
                <VehicleCard
                  key={vehicle?.id}
                  vehicle={vehicle}
                  handleBookNow={handleBookNow}
                  selectMultiple={selectMultiple}
                  selectedVehicles={selectedVehicles}
                  setSelectedVehicles={setSelectedVehicles}
                  canSelectMoreCars={canSelectMoreCars}
                  setEta={setEta}
                  isImmediateJourney={data.plan?.isImmediateJourney}
                />
              ))}
            </Box>
            {!selectMultiple && data?.account?.numberOfPassengers > 1 && (
              <Button
                onClick={() => setSelectMultiple((prevValue) => !prevValue)}
                sx={{
                  margin: '20px 0',
                  width: '350px',
                  '@media (min-width: 1550px)': {
                    width: '730px',
                  },
                }}
              >
                Select Multiple Vehicles
              </Button>
            )}
          </Box>
        </Grid>
        <Grid item xs={5}>
          <BookingSummary
            bookingPreview={preview}
            isLoadingBooking={isLoadingBooking}
            onSubmit={handleMultiBooking}
          />
        </Grid>
      </Grid>
      <Snackbar
        open={showSeatCountWarning}
        autoHideDuration={6000}
        onClose={() => setShowSeatCountWarning(false)}
        action={
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.5 }}
            onClick={() => setShowSeatCountWarning(false)}
          >
            <CloseIcon />
          </IconButton>
        }
      >
        <Alert severity="error">
          You have not selected enough cars for your journey.
        </Alert>
      </Snackbar>
    </>
  )
}

export default TransportOptions
